import rp, { BroadcastEvent, Host } from "@reporting/rp-app-host";
import { Resource } from "@reporting/rp-types/generated/ext";
import { ReportContext } from "@reporting/rp-types/generated/analytics";
import { css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import qs from "qs";
import { dom, Layout } from "@omegagrid/core";
import { Engine, ExtensionEvent } from "../engine/engine";
import { ExtensionBridge, RelayedBroadcastMessage } from "../bridge";
import { CustomAction, CustomParams, ExportType } from "../types";

/** minimum placeholder width/height to show the iframe (same rule as og-code) */
const VISIBILITY_THRESHOLD_SIZE = 10;

/**
 * Loads an `.html` extension from its physical URL (`/extension/{a}/{idProject}/...`).
 * The page bootstraps itself (definition, resources, data - cookie auth) and runs
 * the engine inside the iframe; this component owns the bridge that carries
 * context in and events out.
 *
 * The iframe lives in a container appended to document.body and this component is
 * only the placeholder it is positioned over (the og-code/monaco pattern) -
 * re-parenting the component (dashboard drag, tab switches) then never detaches
 * the iframe, so the extension keeps its window, state and data with no reload.
 * Unlike monaco the container is never removed while hidden: detaching an iframe
 * destroys its browsing context, so hiding is display:none only.
 */
@customElement('rp-extensionhost-sandbox')
export class ExtensionSandboxHost extends Host implements Layout {

	/** same layer as og-code's monaco container */
	static defaultZIndex = 10000;

	static styles = [...Host.styles, css`
		:host {
			display: block;
			height: 100%;
			position: relative;
			background-color: #fff;
		}
	`];

	@property({type: Object})
	resource: Resource;

	@property({ type: Object })
	reportContext: ReportContext;

	@property({type: Number})
	zIndex = ExtensionSandboxHost.defaultZIndex;

	private readonly bridge = new ExtensionBridge();
	private _broadcastId: number;
	private _reloadToken = 0;

	/**
	 * Structural stand-in for the in-iframe engine - satisfies the engine shape the
	 * analytics visualization wrapper consumes; refreshed from every 'load' message.
	 */
	private engineProxy = {
		actions: [] as CustomAction[],
		exportTypes: [] as ExportType[],
		parameters: {} as CustomParams,
		isSavable: false,
		getCustomActions: () => this.engineProxy.actions,
		getExportTypes: () => this.engineProxy.exportTypes,
		invokeAction: (id: string, data?: unknown) => this.bridge.action(id, data),
		export: async (type: ExportType) => this.bridge.export(type),
	};

	constructor() {
		super();
		this.bridge.onLoad = (payload) => {
			Object.assign(this.engineProxy, payload);
			this.dispatchEvent(new ExtensionEvent('load', { engine: this.engineProxy as unknown as Engine }));
		};
		this.bridge.onDetail = (data) => {
			this.dispatchEvent(new ExtensionEvent('detail', { engine: this.engineProxy as unknown as Engine, data }));
		};
		this.bridge.onBroadcast = ({ message }) => {
			// id is a page-local subscriber counter - meaningless (and colliding) here
			const relayed: RelayedBroadcastMessage = { ...message, id: null, __rpexSource: this.bridge.uid };
			rp.broadcast.send(relayed);
		};
	}

	private _portal: HTMLDivElement;
	private _iframe: HTMLIFrameElement;

	/** the body-level element the iframe permanently lives in */
	get portal() {
		if (this._portal) return this._portal;
		this._portal = dom.createElement<HTMLDivElement>('div', {
			style: {
				position: 'absolute',
				zIndex: String(this.zIndex),
				backgroundColor: '#fff',
				overflow: 'hidden',
				display: 'none',
			},
		});
		this._iframe = dom.createElement<HTMLIFrameElement>('iframe', {
			style: { width: '100%', height: '100%', border: 'none' },
		});
		this._portal.appendChild(this._iframe);
		this.bridge.attach(this._iframe);
		return this._portal;
	}

	get src() {
		if (!this.container) return null;
		const query: Record<string, unknown> = { embedded: 1 };

		let idProject: number | string;
		let resourcePath = '';
		if (this.resource) {
			idProject = this.resource.id_custom_page;
			query.resource = this.resource.id_custom_resource;
		} else {
			// whole-page mode (router.app == 'extensions') - the project and resource come
			// from the route: /{a}/extensions/{idProject}/{resourcePath...}
			const path = this.container.router?.path ?? [];
			if (!path.length) return null;
			idProject = path[0];
			resourcePath = path.slice(1).join('/');
		}

		// forward custom params of the SPA route (deep links carry them for the engine)
		const p = this.container.router?.queryParams?.p;
		if (p) query.p = p;
		if (this._reloadToken) query.r = this._reloadToken;
		const a = this.container.ms('extensions').a;
		return `/extension/${a}/${idProject}/${resourcePath}?${qs.stringify(query)}`;
	}

	/** navigate the iframe when the target url changed; the element itself never moves */
	private applySrc() {
		const src = this.src;
		if (!src) return;
		this.portal; // ensure the iframe exists
		const absolute = new URL(src, window.location.origin).href;
		if (this._iframe.src !== absolute) this._iframe.src = absolute;
	}

	/** full page reload with fresh data; edited main resource content is delivered with the next init */
	reload(mainResourceContent?: string) {
		if (mainResourceContent != null) this.bridge.setMainResourceContent(mainResourceContent);
		this.bridge.clearCache();
		this._reloadToken++;
		this.applySrc();
	}

	/** re-render in place (editor preview push) */
	refresh(mainResourceContent?: string) {
		this.bridge.refresh(mainResourceContent);
	}

	updated(props: Map<string, unknown>) {
		this.applySrc();
		if (props.has('reportContext') && this.reportContext) {
			this.bridge.setContext(this.reportContext);
		}
		this.layout();
	}

	/** positions the body-level container over this placeholder (og-code pattern) */
	layout = () => {
		if (!this._portal) return;
		if (!this.isConnected || this.clientWidth < VISIBILITY_THRESHOLD_SIZE || this.clientHeight < VISIBILITY_THRESHOLD_SIZE) {
			this._portal.style.display = 'none';
			return;
		}

		if (this._portal.parentElement !== document.body) {
			document.body.appendChild(this._portal);
		}
		this._portal.style.display = '';
		const offset = dom.getElementOffset(this, document.body);
		dom.setPosition(this._portal, { t: offset.top, l: offset.left });
		dom.setSize(this._portal, { w: this.clientWidth, h: this.clientHeight });
	};

	private _layoutQueued = false;

	/** coalesces bursts (scroll, resize observers) into one layout per frame */
	private queueLayout = () => {
		if (this._layoutQueued) return;
		this._layoutQueued = true;
		requestAnimationFrame(() => {
			this._layoutQueued = false;
			this.layout();
		});
	};

	private _resizeObserver = new ResizeObserver(this.queueLayout);

	private _onBusBroadcast = (e: BroadcastEvent) => {
		if ((e.message as RelayedBroadcastMessage).__rpexSource === this.bridge.uid) return;
		this.bridge.send('broadcast', { message: e.message });
	};

	connectedCallback() {
		super.connectedCallback();
		this._broadcastId ??= rp.broadcast.subscribe(this._onBusBroadcast);
		this._resizeObserver.observe(this);
		// capture phase also catches scrolling of inner containers (dashboards)
		window.addEventListener('scroll', this.queueLayout, true);
		window.addEventListener('resize', this.queueLayout);
		this.queueLayout();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._broadcastId != null) {
			rp.broadcast.unsubscribe(this._broadcastId);
			this._broadcastId = null;
		}
		this._resizeObserver.unobserve(this);
		window.removeEventListener('scroll', this.queueLayout, true);
		window.removeEventListener('resize', this.queueLayout);
		if (this._portal) this._portal.style.display = 'none';
	}

	dispose() {
		this.bridge.dispose();
		this._resizeObserver.disconnect();
		this._portal?.remove();
		this._portal = null;
		this._iframe = null;
	}

	render = () => html``;

}
